import { apiClient } from '@/api/apiClient'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type { CbamModifiers, EuaPriceQuarter } from '@/shared/types/cbamModifiers'

export type CbamSimulation = {
  id: string
  name: string
  organizationId: string
  createdAt: string
  updatedAt?: string
  deletedAt?: string
  modifiers: CbamModifiers
}

type CreateCbamSimulationPayload = {
  name: string
}

type UpdateCbamSimulationPayload = {
  name: string
  modifiers?: CbamModifiers
}

const CBAM_SIMULATIONS_QUERY_KEY = 'cbamSimulations'

export const useGetAllCbamSimulations = (organizationId: string) => {
  return useQuery({
    queryKey: [CBAM_SIMULATIONS_QUERY_KEY, organizationId],
    queryFn: async () => {
      const response = await apiClient.get<CbamSimulation[]>(
        `/v1/organizations/${organizationId}/cbam-simulations`
      )
      return response.data
    }
  })
}

export const useCreateCbamSimulation = (organizationId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [CBAM_SIMULATIONS_QUERY_KEY, organizationId],
    mutationFn: async (payload: CreateCbamSimulationPayload) => {
      const response = await apiClient.post<CbamSimulation>(
        `/v1/organizations/${organizationId}/cbam-simulations`,
        payload
      )

      queryClient.setQueryData<CbamSimulation[]>(
        [CBAM_SIMULATIONS_QUERY_KEY, organizationId],
        oldData => [...(oldData || []), response.data]
      )

      return response.data
    }
  })
}

export const useUpdateCbamSimulation = (organizationId: string, simulationId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [CBAM_SIMULATIONS_QUERY_KEY, organizationId, simulationId],
    mutationFn: async (payload: UpdateCbamSimulationPayload) => {
      const response = await apiClient.patch<CbamSimulation>(
        `/v1/organizations/${organizationId}/cbam-simulations/${simulationId}`,
        payload
      )

      queryClient.setQueryData<CbamSimulation[]>(
        [CBAM_SIMULATIONS_QUERY_KEY, organizationId],
        oldData =>
          oldData?.map(simulation =>
            simulation.id === simulationId ? response.data : simulation
          ) || []
      )

      queryClient.setQueryData<CbamSimulation>(
        [CBAM_SIMULATIONS_QUERY_KEY, organizationId, simulationId],
        response.data
      )

      return response.data
    }
  })
}

export const useDeleteCbamSimulation = (organizationId: string, simulationId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [CBAM_SIMULATIONS_QUERY_KEY, organizationId, simulationId],
    mutationFn: async () => {
      await apiClient.delete(`/v1/organizations/${organizationId}/cbam-simulations/${simulationId}`)

      queryClient.setQueryData<CbamSimulation[]>(
        [CBAM_SIMULATIONS_QUERY_KEY, organizationId],
        oldData => oldData?.filter(simulation => simulation.id !== simulationId) || []
      )
    }
  })
}
